import React, { useContext, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import EventIcon from '@mui/icons-material/Event'
import LocalHospitalIcon from '@mui/icons-material/LocalHospital'
import RequestQuoteIcon from '@mui/icons-material/RequestQuote'
import API from '../../../../services/API'
import { TenantSettingsContext } from '../../../../App'
import CustomDataGrid from '../../../../components/DataGrid/CustomDataGrid'
import { SummaryTile } from '../Invoices/Invoices'

const sourceMeta = {
  both: { label: 'Both', icon: <RequestQuoteIcon fontSize="small" /> },
  Event: { label: 'Events', icon: <EventIcon fontSize="small" /> },
  PTS: { label: 'PTS', icon: <LocalHospitalIcon fontSize="small" /> },
}

const statusColor = {
  Accepted: 'success',
  Converted: 'success',
  Sent: 'primary',
  Declined: 'error',
  Expired: 'default',
  Draft: 'default',
}

const Quotes = () => {
  const [loading, setLoading] = useState(true)
  const [quotes, setQuotes] = useState([])
  const [fetchError, setFetchError] = useState(false)
  const [source, setSource] = useState('both')
  const navigate = useNavigate()
  const { orgConfig } = useContext(TenantSettingsContext)
  const currency = orgConfig?.orgSettings?.currency ?? '£'

  const loadQuotes = () => {
    setLoading(true)
    setFetchError(false)
    API.get('/finance/quotes')
      .then((res) => {
        if (res.status === 200) setQuotes(res.data || [])
        else setFetchError(true)
      })
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false))
  }

  useEffect(() => loadQuotes(), [])

  const filteredQuotes = useMemo(
    () =>
      source === 'both' ? quotes : quotes.filter((q) => q.source === source),
    [quotes, source],
  )

  const summary = useMemo(
    () =>
      filteredQuotes.reduce(
        (acc, quote) => {
          acc.total += quote.total || 0
          if (quote.status === 'Sent') acc.pendingValue += quote.total || 0
          if (['Accepted', 'Converted'].includes(quote.status)) {
            acc.acceptedValue += quote.total || 0
          }
          return acc
        },
        { total: 0, pendingValue: 0, acceptedValue: 0 },
      ),
    [filteredQuotes],
  )

  return (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', md: 'center' }}
        gap={2}
      >
        <Stack spacing={0.5}>
          <Typography variant="title2">Quotes</Typography>
          <Typography variant="body2" color="text.secondary">
            Event and patient transport quotes from one place.
          </Typography>
        </Stack>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={source}
          onChange={(event, nextSource) => nextSource && setSource(nextSource)}
        >
          {Object.entries(sourceMeta).map(([value, meta]) => (
            <ToggleButton key={value} value={value}>
              <Stack direction="row" alignItems="center" gap={0.75}>
                {meta.icon}
                {meta.label}
              </Stack>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>
      <Box>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <SummaryTile
              label="Total Quoted"
              value={`${currency}${summary.total.toFixed(2)}`}
              icon={<RequestQuoteIcon />}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <SummaryTile
              label="Awaiting Response"
              value={`${currency}${summary.pendingValue.toFixed(2)}`}
              icon={<RequestQuoteIcon />}
              tone="warning"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <SummaryTile
              label="Accepted"
              value={`${currency}${summary.acceptedValue.toFixed(2)}`}
              icon={<RequestQuoteIcon />}
              tone="success"
            />
          </Grid>
        </Grid>
      </Box>

      {fetchError && (
        <Paper
          elevation={0}
          sx={{ p: 2, border: '1px solid', borderColor: 'error.light' }}
        >
          <Typography color="error">Unable to load quotes.</Typography>
        </Paper>
      )}

      <CustomDataGrid
        norowsoverlay={
          <Alert severity="info">
            No quotes yet. Build event quotes with the Quote Builder (Clients
            → New Quote), or add a quote to a PTS booking — accepted quotes
            can then be converted to invoices.
          </Alert>
        }
        rows={filteredQuotes}
        loading={loading}
        getRowId={(row) => row._id}
        disableRowSelectionOnClick
        autoHeight
        documentTitle="Quotes"
        columns={[
          { field: 'reference', headerName: 'Reference', minWidth: 140 },
          {
            field: 'source',
            headerName: 'Source',
            minWidth: 120,
            renderCell: ({ row }) => (
              <Chip
                size="small"
                icon={
                  row.source === 'PTS' ? <LocalHospitalIcon /> : <EventIcon />
                }
                label={row.source === 'PTS' ? 'PTS' : 'Event'}
              />
            ),
          },
          {
            field: 'linkedRecord',
            headerName: 'Linked Record',
            minWidth: 240,
            flex: 1,
            valueGetter: (value, row) =>
              row.source === 'PTS'
                ? row.booking?.bookingRef || ''
                : row.event?.title || '',
            renderCell: ({ row }) =>
              row.source === 'PTS' ? (
                <Button
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation()
                    navigate(`/pts/bookings/${row.booking?._id}`)
                  }}
                  disabled={!row.booking?._id}
                >
                  {row.booking?.bookingRef || 'PTS booking'}
                </Button>
              ) : (
                <Button
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation()
                    navigate(`/events/${row.event?._id}`)
                  }}
                  disabled={!row.event?._id}
                >
                  {row.event?.title || 'Event'}
                </Button>
              ),
          },
          {
            field: 'organisation',
            headerName: 'Customer',
            valueGetter: (value, row) => row.organisation?.orgName || '',
            minWidth: 200,
            flex: 1,
          },
          {
            field: 'status',
            headerName: 'Status',
            minWidth: 130,
            renderCell: ({ value }) => (
              <Chip
                size="small"
                label={value || 'Draft'}
                color={statusColor[value] || 'default'}
              />
            ),
          },
          {
            field: 'validUntil',
            headerName: 'Valid Until',
            renderCell: ({ value }) =>
              value ? moment(value).format('DD/MM/YYYY') : '',
            minWidth: 130,
          },
          {
            field: 'total',
            headerName: 'Total',
            renderCell: ({ value }) =>
              `${currency}${Number(value || 0).toFixed(2)}`,
            minWidth: 120,
          },
        ]}
      />
    </Stack>
  )
}

export default Quotes
